import React, { useState } from "react";
import { X } from "lucide-react";
import { useCurrencyUtils } from "../../utils/currencyUtils";
import { createDepositRequestBuyer } from "../../services/buyerDepositRequestServices.ts";
import { useToast } from "../../context/ToastContext.jsx";

const WithdrawOrders = ({ onClose, activePackage = null }) => {
  const [remarks, setRemarks] = useState("");
  const [confirmCancellation, setConfirmCancellation] = useState(false);
  const { showSuccess, showError } = useToast();
  const { displayPriceWithSymbol, convertPrice } = useCurrencyUtils();

  const depositAmount = activePackage?.depositAmount || activePackage?.deposit || 0;

  const handleSubmit = async () => {
    if (!confirmCancellation) return;

    try {
      const response = await createDepositRequestBuyer({
        notes: remarks.trim() || undefined,
      });
      showSuccess("Withdraw request submitted successfully");
      onClose();
    } catch (error) {
      console.error("Error creating deposit request:", error);
      const message = error.response?.data?.message || "Unknown error";
      showError(`Failed to submit withdraw request: ${message}`);
    }
  };

  return (
    <div className="p-6 bg-card min-h-screen">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Withdraw Deposit</h1>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-800 rounded-full transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left - Warning / Information */}
        <div className="space-y-6">
          <div className="bg-page border border-default rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-red-700 mb-3">
                  ⚠️ Important: Package Cancellation
                </h3>
                <div className="text-sm text-red-800 space-y-3">
                  <p className="font-semibold text-base">
                    Withdrawing your deposit will{" "}
                    <strong className="text-red-900">cancel your package</strong>.
                  </p>
                  <p className="font-medium">This action will:</p>
                  <ul className="list-disc list-inside ml-5 space-y-1.5">
                    <li>Terminate your active package immediately</li>
                    <li>Remove your bidding privileges and access</li>
                    <li>Refund your deposit to your account</li>
                    <li>Cancel any pending package requests</li>
                  </ul>
                  <p className="font-bold mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                    Make sure you really want to cancel before proceeding.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right - Form */}
        <div className="lg:sticky lg:top-6 self-start">
          <div className="bg-page border border-default rounded-xl p-6 shadow-lg">
            {/* Deposit Amount */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white/90 mb-2">
                Your Deposit Amount
              </h3>
              <div className="text-4xl font-bold text-primary">
                {displayPriceWithSymbol(convertPrice(depositAmount))}
              </div>
            </div>

            {/* Remarks */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-white/70 mb-2">
                Remarks (optional)
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={4}
                placeholder="Enter any remarks or reason..."
                className="w-full bg-card text-white px-4 py-3 border border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
            </div>

            {/* Confirmation */}
            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmCancellation}
                  onChange={(e) => setConfirmCancellation(e.target.checked)}
                  className="mt-1 h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <span className="text-sm text-white/80 leading-relaxed">
                  I understand that withdrawing my deposit will{" "}
                  <strong className="text-red-500">cancel my package</strong> and
                  remove all bidding privileges. I confirm I want to proceed.
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!confirmCancellation}
              className={`w-full py-3.5 px-6 rounded-lg font-medium transition-all ${confirmCancellation
                  ? "bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-900/20"
                  : "bg-gray-700 text-gray-400 cursor-not-allowed"
                }`}
            >
              {confirmCancellation
                ? "Cancel Package & Withdraw Deposit"
                : "Check the box to proceed"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawOrders;